/** task: Focus Mode (clock ×4). menu: normal clock. pause: clock stops (the Codex). */
export type PanelKind = 'task' | 'menu' | 'pause';
export type PanelSide = 'left' | 'right' | 'center';

export interface PanelApi {
  close(): void;
  rerender(): void;
}

export interface RenderResult {
  /** Called on every game change. Patch text only. Never rebuild buttons here. */
  update?: () => void;
  /** Called before the body is rebuilt or the panel closes. */
  cleanup?: () => void;
}

export interface PanelSpec {
  id: string;
  title: string;
  kind: PanelKind;
  side: PanelSide;
  render: (body: HTMLElement, api: PanelApi) => RenderResult | void;
  onClose?: () => void;
}

export class PanelManager {
  private current: { spec: PanelSpec; el: HTMLElement; body: HTMLElement; result: RenderResult | void } | null = null;
  private previousFocus: HTMLElement | null = null;

  constructor(private onKindChange: (kind: PanelKind | null) => void) {}

  /** Called in capture phase so the dismissal gesture never reaches Phaser. */
  dismissOutside(event: PointerEvent): boolean {
    if (!this.current || this.current.el.contains(event.target as Node)) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.close();
    return true;
  }

  get openId(): string | null {
    return this.current?.spec.id ?? null;
  }

  get kind(): PanelKind | null {
    return this.current?.spec.kind ?? null;
  }

  open(spec: PanelSpec): void {
    this.close();
    this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const el = document.createElement('div');
    el.dataset.panel = spec.id;
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-label', spec.title);
    el.className = `panel ${spec.side === 'center' ? 'wide' : ''}`;
    el.innerHTML = `<div class="panel-head"><span></span><small class="panel-time">${spec.kind === 'task' ? 'Time ×4' : spec.kind === 'pause' ? 'Paused' : 'Time running'}</small><button class="btn small" aria-label="Close menu" title="Close (E / Esc)">✕</button></div><div class="panel-body"></div>`;
    (el.querySelector('.panel-head span') as HTMLElement).textContent = spec.title;
    (el.querySelector('.panel-head button') as HTMLButtonElement).onclick = () => this.close();
    const body = el.querySelector('.panel-body') as HTMLElement;
    const slotId = spec.side === 'left' ? 'panel-left' : spec.side === 'right' ? 'panel-right' : 'panel-center';
    const slot = document.getElementById(slotId) as HTMLElement;
    slot.appendChild(el);
    this.current = { spec, el, body, result: undefined };
    this.current.result = spec.render(body, this.api());
    this.onKindChange(spec.kind);
    (el.querySelector('.panel-head button') as HTMLButtonElement).focus({ preventScroll: true });
  }

  private api(): PanelApi {
    return { close: () => this.close(), rerender: () => this.rerender() };
  }

  rerender(): void {
    const c = this.current;
    if (!c) return;
    c.result?.cleanup?.();
    c.body.innerHTML = '';
    c.result = c.spec.render(c.body, this.api());
  }

  update(): void {
    this.current?.result?.update?.();
  }

  close(): void {
    const c = this.current;
    if (!c) return;
    this.current = null;
    c.result?.cleanup?.();
    c.el.remove();
    c.spec.onClose?.();
    this.onKindChange(null);
    if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
  }
}
